import { maxBy, minBy } from 'lodash';
import { IPoint } from '@/version-types';
import { translatePointAngle } from '../math-kit';
import Polygon, { Point } from './polygon';

export interface IShape {
    [index: string]: any;
    id?: string;
    width: number;
    height: number;
    left: number;
    top: number;
    parentId?: string;
}

/**
 * 获取碰撞的图形 ID
 *
 * @param shape - 碰撞原对象
 * @param collision - 碰撞信息 id, ICollisionInfo
 * @param left - 左顶点 X
 * @param top - 左顶点 Y
 * @param rotate - 旋转角度
 * @param width - 宽度
 * @param height - 高度
 * @returns 碰撞id数组. 未碰撞为空数组
 */
export const getCollisionIds = (shape: IShape, collisions: Array<IShape>): string[] => {
    const ids: Array<string> = [];
    const shapeTran = convertToPolygon(shape);
    // console.log(shapeTran);

    const collisionsTran = collisions.map(convertToPolygon);
    // console.log(collisionsTran);

    collisionsTran.forEach((v) => {
        if (shapeTran.polygon.collidesWith(v.polygon)) {
            ids.push(v.id!);
        }
    });
    return ids;
};

export const convertToPolygon = (shape: IShape) => {
    const { id } = shape;
    const polygon = new Polygon();
    getRotatePosition(shape).forEach((v) => {
        const { x, y } = new Point(v.x, v.y);
        polygon.addPoint(x, y);
    });

    return {
        polygon,
        id,
    };
};

export const getRotatePosition = (shape: IShape, centerPoint?: IPoint) => {
    const { id, left, top, rotate, width, height } = shape;
    const _x = centerPoint?.x ?? width / 2 + left;
    const _y = centerPoint?.y ?? height / 2 + top;
    return [
        translatePointAngle(
            {
                x: left - _x,
                y: top - _y,
            },
            rotate,
        ),
        translatePointAngle(
            {
                x: left - _x + width,
                y: top - _y,
            },
            rotate,
        ),
        translatePointAngle(
            {
                x: left - _x + width,
                y: top - _y + height,
            },
            rotate,
        ),
        translatePointAngle(
            {
                x: left - _x,
                y: top - _y + height,
            },
            rotate,
        ),
    ].map((v) => {
        const { x, y } = v;
        return { x: x + _x, y: y + _y, id };
    });
};

interface IRectInfo {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

export const getRotateRect = ({
    left,
    top,
    width,
    height,
    rotate,
}: {
    left: number;
    top: number;
    width: number;
    height: number;
    rotate: number;
}) => {
    const position = getRotatePosition({ left, top, width, height, rotate });
    const minX = minBy(position, (pos) => pos.x) as {
        x: number;
        y: number;
    };
    const maxX = maxBy(position, (pos) => pos.x) as {
        x: number;
        y: number;
    };
    const minY = minBy(position, (pos) => pos.y) as {
        x: number;
        y: number;
    };
    const maxY = maxBy(position, (pos) => pos.y) as {
        x: number;
        y: number;
    };

    return {
        x1: minX.x,
        y1: minY.y,
        x2: maxX.x,
        y2: maxY.y,
    } as IRectInfo;
};

/**
 * 获取两个矩形相交面积
 *
 * @param rect1 - 矩形 1
 * @param rect2 - 矩形 2
 */
export function getIntersectionArea(rect1: IRectInfo, rect2: IRectInfo) {
    const x1 = Math.max(rect1.x1, rect2.x1);
    const y1 = Math.max(rect1.y1, rect2.y1);
    const x2 = Math.min(rect1.x2, rect2.x2);
    const y2 = Math.min(rect1.y2, rect2.y2);

    const width = x2 - x1;
    const height = y2 - y1;

    if (width <= 0 || height <= 0) {
        return 0;
    }

    return width * height;
}

/**
 * 交集面积大于给定值
 *
 * @param rect1 - 矩形 1
 * @param rect2 - 矩形 2
 * @param threshold - 阈值
 */
export function isIntersectionAreaGreaterThan(rect1: IRectInfo, rect2: IRectInfo, threshold: number) {
    const intersectionArea = getIntersectionArea(rect1, rect2);
    return intersectionArea > threshold;
}
